import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../componentes/Shell.jsx';
import { ConfirmacionGeneral, TONOS_DE_CONFIRMACION } from '../nucleo/componentes/ConfirmacionGeneral.jsx';
import {
  formatearFecha, obtenerDesplazamientoLegible, obtenerFechaDeNegocioHoy, obtenerZonaDetectada,
  resolverPeriodoLocal,
} from '../nucleo/fecha/fechaLocal.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import { useTema } from '../nucleo/tema/ProveedorDeTema.jsx';
import { etiquetaDeMesYAnio } from '../modulos/tablero/logica/agregacion.js';
import { inicialesDePersona, subtituloDePersona } from '../modulos/tablero/logica/marcaDeUltimoAcceso.js';

/**
 * SESIÓN E IDENTIDAD · F-03 (RF-027, RF-218, RN-045d, RN-046a, RN-617).
 *
 * LAS TRES TARJETAS APROBADAS en v5-sesion.html:65-103, completas:
 *   (a) quién está usando la aplicación, con las marcas de tiempo REALES de la
 *       última entrada que el servidor ya guardaba desde el 1/6 (D-318);
 *   (b) «Datos de esta sesión» con sus seis datos, tarjeta que no existía;
 *   (c) cómo puede terminar la sesión, con el enlace informativo a la pantalla de
 *       sesión finalizada — que se abre SOLO PARA MIRARLA y no cierra la sesión.
 *
 * ACCIONES AL PIE: «Mi perfil» (secundaria) ANTES de «Cerrar sesión», y cerrar
 * sesión PIDE CONFIRMACIÓN reutilizando el componente del núcleo (D-277) SIN
 * MODIFICARLO: se le pasan textos ya resueltos y él decide el marco entero.
 *
 * QUIEN NUNCA HA ENTRADO dice «Aún no entra», nunca una fecha inventada (RN-617).
 */
export default function Sesion() {
  const { sesion, cerrarSesion } = useSesion();
  const { modo, temaEfectivo, modos } = useTema();

  const [confirmando, setConfirmando] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [enLinea, setEnLinea] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));

  useEffect(() => {
    const conectar = () => setEnLinea(true);
    const desconectar = () => setEnLinea(false);
    window.addEventListener('online', conectar);
    window.addEventListener('offline', desconectar);
    return () => {
      window.removeEventListener('online', conectar);
      window.removeEventListener('offline', desconectar);
    };
  }, []);

  const zona = obtenerZonaDetectada();
  const hoy = obtenerFechaDeNegocioHoy();
  const idPeriodo = sesion?.idPeriodoActual ?? resolverPeriodoLocal(hoy);

  /* Las dos personas del hogar, en el orden que entrega el servidor. */
  const personas = sesion?.usuariosDelHogar ?? [];

  async function confirmarCierre() {
    setCerrando(true);
    try {
      await cerrarSesion();
    } finally {
      // Si la navegación no ocurriera, la pantalla no se queda atrapada en «en curso».
      setCerrando(false);
      setConfirmando(false);
    }
  }

  return (
    <Shell titulo="Sesión" volverA="/ajustes">
      {/* (a) ---------------------------------------------------------------- */}
      <div className="card lista">
        <h3>Quién está usando la aplicación</h3>

        {personas.length === 0 && (
          <div className="fila">
            <div className="crece">
              <b>{sesion?.nombre}</b>
              <small>Clave: {sesion?.claveUsuario}</small>
            </div>
            <span className="chip ok">Tú</span>
          </div>
        )}

        {personas.map((persona) => (
          <div className="fila" key={persona.idUsuario} data-prueba="persona-del-hogar" data-id={persona.idUsuario}>
            <div className="punto" aria-hidden="true">{inicialesDePersona(persona.nombre)}</div>
            <div className="crece">
              <b>{persona.nombre}</b>
              {/* RN-617 · la marca es el último acceso que el servidor ya guarda,
                  convertido a la zona de este dispositivo. */}
              <small data-prueba="marca-de-persona">{subtituloDePersona(persona)}</small>
            </div>
            <span className={`chip${persona.esUsuarioEnSesion ? ' ok' : ''}`}>
              {persona.esUsuarioEnSesion ? 'Tú' : 'Otra persona usuaria'}
            </span>
          </div>
        ))}

        <div className="aviso i">
          Las dos ven todo lo del hogar; <b>cada quien edita solo lo que capturó</b>. Los catálogos
          los edita cualquiera de las dos.
        </div>
      </div>

      {/* (b) ---------------------------------------------------------------- */}
      <div className="card lista" data-prueba="datos-de-esta-sesion">
        <h3>Datos de esta sesión</h3>

        <div className="fila">
          <div className="crece"><b>Vigencia</b></div>
          <span className="ayuda">30 días desde el último uso</span>
        </div>
        <div className="fila">
          <div className="crece"><b>Fecha local del dispositivo</b></div>
          <span className="ayuda" data-prueba="fecha-de-la-sesion">{formatearFecha(hoy)}</span>
        </div>
        <div className="fila">
          <div className="crece"><b>Zona detectada del dispositivo</b></div>
          <span className="ayuda">
            {zona ? `${zona} (${obtenerDesplazamientoLegible()})` : 'no disponible'}
          </span>
        </div>
        <div className="fila">
          <div className="crece"><b>Periodo actual</b></div>
          <span className="ayuda" data-prueba="periodo-de-la-sesion">
            {idPeriodo} · {etiquetaDeMesYAnio(hoy)}
          </span>
        </div>
        <div className="fila">
          <div className="crece"><b>Estado de conexión</b></div>
          <span className="ayuda">{enLinea ? 'En línea' : 'Sin conexión'}</span>
        </div>
        <div className="fila">
          <div className="crece"><b>Tema de este dispositivo</b></div>
          <span className="ayuda">
            {temaEfectivo === modos.OSCURO ? 'Oscuro' : 'Claro'}
            {modo === modos.AUTOMATICO ? ' · como este dispositivo' : ' · elegido aquí'}
          </span>
        </div>

        <div className="aviso w">
          La zona se detecta de tu dispositivo y no se puede cambiar en ninguna pantalla. El tema
          tampoco es de tu sesión: es de este dispositivo y se cambia en <b>Ajustes › Apariencia</b>,
          así que puede ser distinto en otro aparato tuyo.
        </div>
      </div>

      {/* (c) ---------------------------------------------------------------- */}
      <div className="card lista">
        <h3>Cómo puede terminar tu sesión</h3>

        <div className="fila">
          <div className="crece"><b>La cierras tú</b></div>
          <span className="ayuda">Desde el botón de abajo</span>
        </div>
        <div className="fila">
          <div className="crece"><b>Caduca por no usarla</b></div>
          <span className="ayuda">30 días sin entrar</span>
        </div>
        <div className="fila">
          <div className="crece"><b>Te quitan el acceso</b></div>
          <span className="ayuda" style={{ color: 'var(--err)', fontWeight: 700 }}>Al instante</span>
        </div>

        <div className="aviso w">
          Si la otra persona del hogar te quita el acceso, tu sesión se cierra en ese momento y en
          todos tus dispositivos, sin esperar a que caduque. Lo mismo pasa si te restablece la
          contraseña.
          {/* Se abre SOLO PARA MIRARLA: no cierra la sesión (RF-218c). */}
          <div style={{ marginTop: 7 }}>
            <Link to="/sesion-finalizada" data-prueba="ver-sesion-finalizada">
              Ver cómo se ve cuando eso ocurre →
            </Link>
          </div>
        </div>
      </div>

      {/* «Mi perfil» va ANTES de «Cerrar sesión», y es la acción secundaria. */}
      <div style={{ display: 'flex', gap: 8, margin: '10px 12px', flexWrap: 'wrap' }}>
        <Link className="btn sec" to="/ajustes/perfil" style={{ flex: '1 1 140px' }} data-prueba="ir-a-mi-perfil">
          Mi perfil
        </Link>
        <button
          className="btn"
          type="button"
          style={{ flex: '1 1 140px' }}
          onClick={() => setConfirmando(true)}
          data-prueba="cerrar-sesion"
        >
          Cerrar sesión
        </button>
      </div>

      {/* Fila 14 del inventario del incremento 15 (RF-222/RF-223): salió la jerga técnica
          («caché», «armazón cacheado», «dato de negocio»). */}
      <div className="info" data-prueba="sesion-cierre-datos">
        Al cerrar sesión, tus datos no quedan guardados en este dispositivo. Tu preferencia de tema
        sí se conserva.
      </div>

      {/* La MISMA pieza de confirmación del resto de la aplicación, sin tocarla. */}
      <ConfirmacionGeneral
        abierta={confirmando}
        pregunta="¿Cerrar tu sesión?"
        consecuencia="Tendrás que escribir tu clave y tu contraseña para volver a entrar. Lo que ya capturaste no se pierde: queda guardado para las dos personas del hogar."
        rotuloDeAceptar="Sí, cerrar sesión"
        rotuloDeCancelar="No, seguir aquí"
        rotuloEnCurso="Cerrando…"
        tono={TONOS_DE_CONFIRMACION.NEUTRAL}
        enCurso={cerrando}
        alAceptar={confirmarCierre}
        alCancelar={() => setConfirmando(false)}
        datoDePrueba="confirmacion-de-cierre-de-sesion"
      />
    </Shell>
  );
}
